// src/api/auth.js
import apiClient from "../utils/apiClient";

export function login(data) {
  const { user, token } = data;
  // An ordinary login is never a support session. Any leftover impersonation
  // state on this origin belongs to a session that ended without a clean exit
  // (revoked, superseded, expired mid-tab); inheriting it would paint a support
  // banner over a real customer's account.
  localStorage.removeItem('vc-impersonation');
  localStorage.removeItem('vc-impersonation-prev');
  // Nor is it a session opened from a distributor's dashboard.
  localStorage.removeItem('vc-partner-session');
  localStorage.removeItem('vc-partner-session-prev');
  localStorage.setItem('user', JSON.stringify(user));
  localStorage.setItem('token', token);
  // Notify listeners (e.g. SocketProvider) that auth state changed
  window.dispatchEvent(new Event('vc-auth-change'));
  return { user };
}

/**
 * Unsubscribe from browser push notifications before logout.
 * Removes ALL web push subscriptions for this user on the server.
 * Non-blocking — logout proceeds even if this fails.
 */
async function cleanupPushSubscriptions() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;

    // Tell server to remove all web push subscriptions for this user
    await apiClient.post('/notifications/push-unsubscribe-all');
    console.log('[Push] Cleaned up web push subscriptions on logout');
  } catch (err) {
    // Non-critical — proceed with logout regardless
    console.warn('[Push] Failed to cleanup push subscriptions on logout:', err.message);
  }
}

/**
 * Revoke this device's session server-side.
 *
 * The counterpart to POST /auth/logout. Non-blocking by design: the user has
 * asked to sign out, so a network failure must not leave them signed in — the
 * token is discarded locally regardless and the server's idle window retires
 * the orphaned row.
 */
async function revokeServerSession() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;
    await apiClient.post('/auth/logout');
  } catch (err) {
    console.warn('[Auth] Server-side session revoke failed:', err.message);
  }
}

export async function logout() {
  // ── Platform support session ──
  // Logging out of an impersonated session must NOT run the normal teardown:
  // push-unsubscribe-all would delete the real customer's browser
  // subscriptions, silently stopping THEIR notifications because support
  // looked at their account. End the support session instead and hand the
  // operator back their own.
  const { isImpersonating, clearImpersonation } = await import('./impersonation');
  if (isImpersonating()) {
    try {
      await apiClient.post('/auth/impersonate/stop');
    } catch (err) {
      // Already revoked or expired — clear locally regardless.
    }
    const { restored, returnUrl } = clearImpersonation();
    if (!restored && returnUrl) window.location.href = returnUrl;
    return;
  }

  // ── Institute opened from a distributor's dashboard ──
  // Its owner account has no password, so a plain sign-out would strand the
  // person on a login page they cannot get past. End the session and send
  // them back to their dashboard. No push teardown: the subscriptions on this
  // account are whoever else opened it.
  const { isPartnerSession, clearPartnerSession } = await import('./partnerSession');
  if (isPartnerSession()) {
    let serverReturnUrl = null;
    try {
      const { data } = await apiClient.post('/auth/partner-login/sign-out');
      serverReturnUrl = data?.returnUrl || null;
    } catch (err) {
      // Already ended or expired — clear locally regardless.
    }
    const { restored, returnUrl } = clearPartnerSession();
    const back = returnUrl || serverReturnUrl;
    if (!restored && back) window.location.href = back;
    return;
  }

  // Clean up push subscriptions BEFORE removing the token
  await cleanupPushSubscriptions();

  // Tell the server this device is signing out, so its UserSession row is
  // revoked. Without this the row stays active for the token's full 30-day
  // life and every browser the user has ever signed in from piles up on the
  // Active Sessions screen. Best-effort: an offline logout must still clear
  // the credentials locally, and the server's idle window catches the rest.
  await revokeServerSession();

  localStorage.removeItem('user');
  localStorage.removeItem('token');
  localStorage.removeItem('selectedRole');
  window.dispatchEvent(new Event('vc-auth-change'));
}

export function getCurrentUser() {
  const user = localStorage.getItem('user');
  return user ? JSON.parse(user) : null;
}

export function isLoggedIn() {
  return !!localStorage.getItem('user');
}